import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { SafeResourceUrl } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';

import { switchMap } from 'rxjs/operators';

import { trackByIndex, trackByProp } from '@recruitee/common';
import { InfoboxComponent } from '@recruitee/components/infobox';
import { TranslatePipe } from '@recruitee/i18n';
import {
  BoxComponent,
  ButtonComponent,
  DividerComponent,
  HeadingComponent,
  IconComponent,
} from '@recruitee/user-interface';

import { FeatureItemComponent } from '../../../shared/plan-picker/2024/feature-item/feature-item.component';
import { ProductOrderParamsIT } from '../product-order.types';
import { ProductOrderMigrationLogicService } from './product-order-migration-logic.service';
import type { MigrationDataUI } from './product-order-migration-ui.types';

@Component({
  selector: 'rt-order-migration-step',
  imports: [
    CommonModule,
    HeadingComponent,
    BoxComponent,
    TranslatePipe,
    ButtonComponent,
    IconComponent,
    DividerComponent,
    InfoboxComponent,
    FeatureItemComponent,
  ],
  templateUrl: './product-order-migration-step.component.html',
  styleUrl: './product-order-migration-step.component.less',
  providers: [ProductOrderMigrationLogicService],
})
export class ProductOrderMigrationStepComponent {
  public readonly trackByLabel = trackByProp<{ label: string }>({ prop: 'label' });
  public readonly trackByIndex = trackByIndex;

  public data: MigrationDataUI;
  public videoUrl?: SafeResourceUrl;

  private params$ = inject(ProductOrderParamsIT);
  constructor(
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private service: ProductOrderMigrationLogicService,
    private destroyRef: DestroyRef,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit() {
    this.params$
      .pipe(
        switchMap(params => this.service.getData$(params.productId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(data => {
        this.data = data;
        if (data.topCard.ytVideoId) {
          this.videoUrl = this.getYouTubeUrl(data.topCard.ytVideoId);
        }
        this.cdRef.detectChanges();
      });
  }

  public continue() {
    this.service.continue(this.route);
  }

  public back() {
    this.service.back();
  }

  private getYouTubeUrl(id: string): SafeResourceUrl {
    const url = `https://www.youtube.com/embed/${id}?autoplay=1&origin=https://recruitee.com`;
    // eslint-disable-next-line ban/ban
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
